/**
 * PlacementTableFilters - Filter controls for PlacementTableView
 *
 * Sticky filter bar with status chips, search input, and sort controls.
 */

import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Chip,
  InputAdornment,
  Stack,
  TextField,
} from "@mui/material";
import React from "react";

interface PlacementTableFiltersProps {
  filterMode: 'all' | 'placed' | 'unplaced';
  onFilterModeChange: (mode: 'all' | 'placed' | 'unplaced') => void;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onSortChange: (column: any) => void;
  filterCounts: {
    all: number;
    placed: number;
    unplaced: number;
  };
}

export const PlacementTableFilters: React.FC<PlacementTableFiltersProps> = ({
  filterMode,
  onFilterModeChange,
  searchQuery,
  onSearchQueryChange,
  filterCounts,
}) => {
  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        bgcolor: 'background.paper',
        borderBottom: '1px solid rgba(0,0,0,0.06)',
        px: 2,
        py: 1.5,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        {/* Status Filter Chips */}
        <Stack direction="row" spacing={0.75}>
          <Chip
            label={`All ${filterCounts.all}`}
            onClick={() => onFilterModeChange('all')}
            size="small"
            sx={{
              height: 24,
              fontSize: '11px',
              fontWeight: 600,
              cursor: 'pointer',
              bgcolor: filterMode === 'all' ? 'rgba(0,0,0,0.08)' : 'transparent',
              border: filterMode === 'all' ? '1px solid rgba(0,0,0,0.15)' : '1px solid rgba(0,0,0,0.08)',
              color: 'text.primary',
              '&:hover': {
                bgcolor: 'rgba(0,0,0,0.06)',
              },
              '& .MuiChip-label': { px: 1.5 },
            }}
          />
          <Chip
            label={`Placed ${filterCounts.placed}`}
            onClick={() => onFilterModeChange('placed')}
            size="small"
            sx={{
              height: 24,
              fontSize: '11px',
              fontWeight: 600,
              cursor: 'pointer',
              bgcolor: filterMode === 'placed' ? 'rgba(16, 185, 129, 0.1)' : 'transparent',
              border: filterMode === 'placed' ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid rgba(0,0,0,0.08)',
              color: filterMode === 'placed' ? '#10B981' : 'text.secondary',
              '&:hover': {
                bgcolor: 'rgba(16, 185, 129, 0.08)',
              },
              '& .MuiChip-label': { px: 1.5 },
            }}
          />
          <Chip
            label={`Unplaced ${filterCounts.unplaced}`}
            onClick={() => onFilterModeChange('unplaced')}
            size="small"
            sx={{
              height: 24,
              fontSize: '11px',
              fontWeight: 600,
              cursor: 'pointer',
              bgcolor: filterMode === 'unplaced' ? 'rgba(239, 68, 68, 0.1)' : 'transparent',
              border: filterMode === 'unplaced' ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid rgba(0,0,0,0.08)',
              color: filterMode === 'unplaced' ? '#EF4444' : 'text.secondary',
              '&:hover': {
                bgcolor: 'rgba(239, 68, 68, 0.08)',
              },
              '& .MuiChip-label': { px: 1.5 },
            }}
          />
        </Stack>

        {/* Search Input */}
        <TextField
          placeholder="Search tail number..."
          value={searchQuery}
          onChange={(e) => onSearchQueryChange(e.target.value)}
          size="small"
          sx={{
            minWidth: 220,
            '& .MuiOutlinedInput-root': {
              fontSize: '14px',
              fontWeight: 500,
              height: 40,
              borderRadius: '8px',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'grey.300',
              },
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'grey.200',
            },
            '& .MuiOutlinedInput-input::placeholder': {
              color: 'grey.500',
              opacity: 1,
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'grey.600' }} />
              </InputAdornment>
            ),
          }}
        />
      </Stack>
    </Box>
  );
};
